// @flow
import * as React from 'react'
import PlaceIcon from 'mz-icons/place'
import AiportIcon from 'mz-icons/airport'
import PortIcon from 'mz-icons/port'
import FavouritePlaceIcon from 'mz-icons/star'
import TrainIcon from 'mz-icons/train'
import GoTransitIcon from 'mz-icons/go-transit'
import { FormattedMessage } from 'mz-intl'
import getPlaceDetails from 'mz-sdk/services/places/getPlaceDetails'
import getSuggestions from 'mz-sdk/services/places/getSuggestions'
import type { SuggestionLocation } from 'mz-sdk/models/location'
import { Option, PlaceTypeLabel, PlaceInfo, IconWrapper } from './elements'

// Types
import type { Props as SelectFieldProps } from '../SelectField'
import messages from './messages.intl'
import EmptySearchResults from '../SelectField/EmptySearchResults'
import SelectField from '../SelectField'

export type Props = SelectFieldProps & {
  placeholder?: React.Node,
  bias?: SuggestionLocation,
  otherParams?: Object,
  customSuggestionsGetter?: (
    query: string,
    bias?: SuggestionLocation,
    otherParams?: any
  ) => Promise<$ReadOnlyArray<SuggestionLocation>>,
}

// Internals
const ICONS_MAP = {
  place: PlaceIcon,
  airport: AiportIcon,
  port: PortIcon,
  train_station: TrainIcon,
  gotransit_station: GoTransitIcon,
}

const getBiasedSuggestions = (
  query: string,
  bias?: SuggestionLocation,
  otherParams?: any
): Promise<Array<SuggestionLocation>> => {
  const resolver = (biasPlace) => getSuggestions(query, biasPlace, otherParams)
  if (bias && !(bias.lat && bias.lng) && bias.place_id) {
    return getPlaceDetails(bias.place_id).then(resolver)
  }
  return resolver(bias)
}

/**
 * A field to select place object.
 */
class PlaceField extends React.Component<Props> {
  static defaultProps = SelectField.defaultProps

  requestPlaces = (query: string): Promise<Array<SuggestionLocation>> => {
    if (!query) return Promise.resolve([])
    const { otherParams, bias, customSuggestionsGetter } = this.props
    return customSuggestionsGetter
      ? (customSuggestionsGetter(query, bias, otherParams): any)
      : getBiasedSuggestions(query, bias, otherParams)
  }

  renderPlaceToString = (place: SuggestionLocation) =>
    place.display || place.favorite_name

  renderIcon = (): React.Node => {
    const {
      input: { value },
    } = this.props
    const type = (value && value.type) || 'place'
    const Icon =
      value && value.favorite_id
        ? FavouritePlaceIcon
        : ICONS_MAP[type] || ICONS_MAP.place
    return <Icon aria-hidden="true" />
  }

  renderEmpty = (query: string) => {
    if (!query) return null
    return (
      <EmptySearchResults
        query={query}
        emptyResultsMessage={
          <FormattedMessage
            message={messages.NO_PLACE_FOUND}
            values={{ query: <b>{query}</b> }}
          />
        }
      />
    )
  }

  renderOption = (place: SuggestionLocation) => {
    const type = place.type || 'place'
    const isFavourite = !!place.favorite_id
    const Icon = isFavourite
      ? FavouritePlaceIcon
      : ICONS_MAP[type] || ICONS_MAP.place
    return (
      <Option>
        <IconWrapper>
          <Icon />
        </IconWrapper>
        <PlaceInfo>
          {place.display}
          {type && (
            <PlaceTypeLabel $favourite={isFavourite}>
              {!isFavourite && type === 'airport' && (
                <FormattedMessage message={messages.AIRPORT} />
              )}
              {!isFavourite && type === 'port' && (
                <FormattedMessage message={messages.PORT} />
              )}
              {isFavourite && place.favorite_name}
            </PlaceTypeLabel>
          )}
        </PlaceInfo>
      </Option>
    )
  }

  getOptionsHash() {
    const { bias } = this.props
    if (!bias) return ''
    const { lat = '', lng = '' } = bias
    const hash = `${lat}_${lng}`

    return hash
  }

  render() {
    const { placeholder } = this.props
    return (
      <SelectField
        key={this.getOptionsHash()}
        emptyValue={null}
        {...this.props}
        renderPrefix={this.renderIcon}
        placeholder={
          placeholder || <FormattedMessage message={messages.PLACEHOLDER} />
        }
        searchableOptions={{
          ...this.props.searchableOptions,
          requestOptions: this.requestPlaces,
          renderEmpty: this.renderEmpty,
        }}
        renderValue={this.renderPlaceToString}
        renderOption={this.renderOption}
      />
    )
  }
}

export default PlaceField
